"use client"
import { useState } from "react"
import { assessmentQuestions } from "../data/assessmentQuestions"

const AssessmentPage = ({ userData, assessmentData, onNext, onBack, onAssessmentDataUpdate }) => {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0)
  const [answers, setAnswers] = useState(assessmentData.answers || {})
  const [isSubmitting, setIsSubmitting] = useState(false)

  const currentQuestion = assessmentQuestions[currentQuestionIndex]
  const totalQuestions = assessmentQuestions.length
  const progress = ((currentQuestionIndex + 1) / totalQuestions) * 100

  const handleAnswerSelect = (questionId, answer) => {
    const newAnswers = {
      ...answers,
      [questionId]: answer,
    }
    setAnswers(newAnswers)
    onAssessmentDataUpdate({ answers: newAnswers })
  }

  const handleNext = () => {
    if (currentQuestionIndex < totalQuestions - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1)
    }
  }

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1)
    }
  }

  const handleComplete = async () => {
    setIsSubmitting(true)
    const submissionData = {
      userData,
      assessmentData: { answers },
      timestamp: new Date().toISOString(),
    }
    try {
      console.log("Submitting assessment data:", submissionData)
      await new Promise((resolve) => setTimeout(resolve, 1000))
      onNext()
    } catch (error) {
      console.error("Error submitting assessment:", error)
      alert("There was an error submitting your assessment. Please try again.")
    } finally {
      setIsSubmitting(false)
    }
  }

  const isCurrentQuestionAnswered = answers[currentQuestion.id] !== undefined
  const isLastQuestion = currentQuestionIndex === totalQuestions - 1

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-50 via-white to-blue-50 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/subtle-white-feathers.png')] opacity-20 z-0"></div>

      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 animate-fade-in">GTM Assessment</h1>
            <div className="flex items-center space-x-4 md:space-x-6">
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-green-500 text-white rounded-full flex items-center justify-center text-sm font-semibold">
                  ✓
                </div>
                <span className="text-sm font-medium text-green-600 hidden sm:block">Registration</span>
              </div>
              <div className="flex-1 h-0.5 bg-gray-200"></div>
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-cyan-600 text-white rounded-full flex items-center justify-center text-sm font-semibold animate-pulse">
                  2
                </div>
                <span className="text-sm font-medium text-cyan-600 hidden sm:block">Assessment</span>
              </div>
              <div className="flex-1 h-0.5 bg-gray-200"></div>
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-gray-200 text-gray-500 rounded-full flex items-center justify-center text-sm font-semibold">
                  3
                </div>
                <span className="text-sm font-medium text-gray-500 hidden sm:block">Results</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Progress Bar */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">
              Question {currentQuestionIndex + 1} of {totalQuestions}
            </span>
            <span className="text-sm font-medium text-gray-700">{Math.round(progress)}% Complete</span>
          </div>
          <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-cyan-600 to-blue-600 transition-all duration-500"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 relative z-10">
        <div className="card bg-white rounded-2xl shadow-lg p-8 animate-fade-in-up">
          <div className="mb-8">
            <div className="flex items-center mb-4">
              <div className="w-12 h-12 bg-cyan-100 rounded-full flex items-center justify-center mr-4">
                <span className="text-cyan-600 font-semibold text-lg">{currentQuestion.category.charAt(0)}</span>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">{currentQuestion.category}</h3>
                <p className="text-sm text-gray-600">GTM Assessment Dimension</p>
              </div>
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">{currentQuestion.question}</h2>
            {currentQuestion.description && (
              <p className="text-gray-600 mb-6 max-w-2xl">{currentQuestion.description}</p>
            )}
          </div>
          <div className="space-y-4 mb-8">
            {currentQuestion.options.map((option, index) => (
              <button
                key={index}
                onClick={() => handleAnswerSelect(currentQuestion.id, option)}
                className={`w-full text-left p-4 rounded-lg border-2 transition-all duration-200 hover:shadow-md ${
                  answers[currentQuestion.id] === option
                    ? "border-cyan-500 bg-cyan-50 text-cyan-900"
                    : "border-gray-200 hover:border-cyan-300 hover:bg-cyan-50/50"
                }`}
                aria-pressed={answers[currentQuestion.id] === option}
                disabled={isSubmitting}
              >
                <div className="flex items-center">
                  <div
                    className={`w-5 h-5 rounded-full border-2 mr-3 flex items-center justify-center ${
                      answers[currentQuestion.id] === option ? "border-cyan-500 bg-cyan-500" : "border-gray-300"
                    }`}
                  >
                    {answers[currentQuestion.id] === option && (
                      <div className="w-3 h-3 bg-white rounded-full"></div>
                    )}
                  </div>
                  <span className="font-medium text-gray-900">{option.text}</span>
                </div>
                {option.description && (
                  <p className="text-sm text-gray-600 ml-8 mt-1">{option.description}</p>
                )}
              </button>
            ))}
            {!isCurrentQuestionAnswered && (
              <p className="text-red-500 text-sm mt-2 animate-fade-in">
                Please select an answer to continue
              </p>
            )}
          </div>
          <div className="flex justify-between">
            <button
              onClick={currentQuestionIndex === 0 ? onBack : handlePrevious}
              className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 hover:scale-105 transition-all duration-200"
              disabled={isSubmitting}
            >
              {currentQuestionIndex === 0 ? "Back to Registration" : "Previous"}
            </button>
            {isLastQuestion ? (
              <button
                onClick={handleComplete}
                disabled={!isCurrentQuestionAnswered || isSubmitting}
                className={`px-8 py-3 rounded-lg font-semibold transition-all duration-200 flex items-center justify-center ${
                  isCurrentQuestionAnswered && !isSubmitting
                    ? "btn-primary bg-gradient-to-r from-cyan-600 to-blue-600 hover:shadow-xl hover:scale-105"
                    : "bg-gray-300 text-gray-500 cursor-not-allowed"
                }`}
              >
                {isSubmitting ? (
                  <>
                    <svg
                      className="animate-spin h-5 w-5 mr-2 text-white"
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Submitting...
                  </>
                ) : (
                  "Complete Assessment"
                )}
              </button>
            ) : (
              <button
                onClick={handleNext}
                disabled={!isCurrentQuestionAnswered}
                className={`px-8 py-3 rounded-lg font-semibold transition-all duration-200 ${
                  isCurrentQuestionAnswered
                    ? "btn-primary bg-gradient-to-r from-cyan-600 to-blue-600 hover:shadow-xl hover:scale-105"
                    : "bg-gray-300 text-gray-500 cursor-not-allowed"
                }`}
              >
                Next Question
              </button>
            )}
          </div>
        </div>
      </main>
    </div>
  )
}

export default AssessmentPage